import { useEffect, useState, useCallback, useMemo } from "react";
import { Loader2, Download } from "lucide-react";
import { toast } from "sonner";
import { admin, downloadCsv } from "../components/admin/adminApi";
import AdminStats from "../components/admin/AdminStats";
import AdminOrders from "../components/admin/AdminOrders";
import AdminInventory from "../components/admin/AdminInventory";
import AdminPromos from "../components/admin/AdminPromos";
import AdminEmails from "../components/admin/AdminEmails";
import { AdminSubscribers, AdminReviews } from "../components/admin/AdminLists";

function LoginForm({ onAuthed }) {
  const [pw, setPw] = useState("");
  const [loading, setLoading] = useState(false);
  const login = async (e) => {
    e.preventDefault(); setLoading(true);
    try { await admin.post("/admin/login", { password: pw }); onAuthed(); }
    catch { toast.error("Invalid password"); }
    finally { setLoading(false); }
  };
  return (
    <div className="min-h-screen bg-black pt-32 pb-20 px-6">
      <form onSubmit={login} className="max-w-sm mx-auto bl-glass border border-white/10 p-8 space-y-4">
        <div className="bl-eyebrow text-[color:var(--bl-gold)]">Owner Access</div>
        <div className="font-display text-white text-3xl uppercase">Admin</div>
        <input type="password" required value={pw} onChange={e => setPw(e.target.value)} data-testid="admin-password" placeholder="Password"
          className="w-full bg-transparent border border-white/15 focus:border-[color:var(--bl-blue)] outline-none px-3 py-3 text-white font-mono text-sm" />
        <button data-testid="admin-login" disabled={loading} className="w-full bl-btn primary">
          {loading ? <Loader2 size={14} className="animate-spin" /> : "Sign In"}
        </button>
        <p className="font-mono text-[10px] tracking-[0.2em] text-white/40 text-center leading-relaxed">Session secured via HttpOnly cookie</p>
      </form>
    </div>
  );
}

export default function Admin() {
  const [authed, setAuthed] = useState(null); // null=checking, true=in, false=out
  const [orders, setOrders] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState(null);
  const [tab, setTab] = useState("dashboard");
  const [orderFilter, setOrderFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [exporting, setExporting] = useState(false);

  const onUnauthed = useCallback(() => setAuthed(false), []);

  useEffect(() => {
    admin.get("/admin/session").then(() => setAuthed(true)).catch(() => setAuthed(false));
  }, []);

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (orderFilter !== "all") params.set("status", orderFilter);
      if (search) params.set("q", search);
      const [o, rv, st] = await Promise.all([
        admin.get(`/admin/orders?${params.toString()}`), admin.get(`/admin/reviews`), admin.get(`/admin/stats`),
      ]);
      setOrders(o.data.orders); setReviews(rv.data.reviews); setStats(st.data);
    } catch (err) {
      if (err?.response?.status === 401) setAuthed(false);
    }
  }, [orderFilter, search]);

  useEffect(() => {
    if (!authed) return;
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [authed, load]);

  const exportOrders = async () => {
    setExporting(true);
    await downloadCsv(`/admin/orders/export.csv`, "biolabz-orders", "orders CSV");
    setExporting(false);
  };
  const logout = async () => {
    try { await admin.post("/admin/logout", {}); } catch { /* cookie may already be expired */ }
    setAuthed(false);
  };

  const pendingReviews = useMemo(() => reviews.filter(r => !r.approved).length, [reviews]);

  if (authed === null) {
    return <div className="min-h-screen bg-black pt-32 pb-20 px-6 flex items-center justify-center"><Loader2 className="animate-spin text-white/40" /></div>;
  }
  if (!authed) return <LoginForm onAuthed={() => setAuthed(true)} />;

  const tabs = [
    { k: "dashboard", label: "Dashboard" },
    { k: "orders", label: `Orders (${orders.length})` },
    { k: "inventory", label: `Inventory${stats?.low_stock_count ? ` · ${stats.low_stock_count} low` : ""}` },
    { k: "promos", label: "Promo Codes" },
    { k: "subscribers", label: "Subscribers" },
    { k: "emails", label: "Emails" },
    { k: "reviews", label: `Reviews (${reviews.length}${pendingReviews ? ` · ${pendingReviews} new` : ""})` },
  ];

  return (
    <div className="min-h-screen bg-black pt-32 pb-20 px-6">
      <div className="max-w-[1200px] mx-auto">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <div>
            <div className="bl-eyebrow text-[color:var(--bl-gold)]">Owner Console</div>
            <h1 className="bl-heading text-white text-4xl mt-2">Admin</h1>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={exportOrders} disabled={exporting} data-testid="admin-export-csv" className="bl-btn gold text-[11px] py-2 px-4 inline-flex items-center gap-2">
              {exporting ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />} Export CSV
            </button>
            <button onClick={logout} data-testid="admin-logout" className="bl-btn">Sign Out</button>
          </div>
        </div>

        <div className="flex gap-2 mb-8 flex-wrap">
          {tabs.map(t => (
            <button key={t.k} onClick={() => setTab(t.k)} data-testid={`admin-tab-${t.k}`}
              className={`px-4 py-2 font-mono text-[11px] tracking-[0.25em] uppercase border ${
                tab === t.k ? "border-[color:var(--bl-blue)] text-[color:var(--bl-blue)]" : "border-white/15 text-white/70 hover:text-white"}`}>
              {t.label}
            </button>
          ))}
        </div>

        {tab === "dashboard" && <AdminStats stats={stats} onOpenInventory={() => setTab("inventory")} />}
        {tab === "orders" && (
          <AdminOrders orders={orders} filter={orderFilter} setFilter={setOrderFilter} search={search} setSearch={setSearch} refresh={load} />
        )}
        {tab === "inventory" && <AdminInventory onUnauthed={onUnauthed} onSaved={load} />}
        {tab === "promos" && <AdminPromos onUnauthed={onUnauthed} />}
        {tab === "subscribers" && <AdminSubscribers onUnauthed={onUnauthed} />}
        {tab === "emails" && <AdminEmails admin={admin} onUnauthed={onUnauthed} />}
        {tab === "reviews" && <AdminReviews reviews={reviews} refresh={load} />}
      </div>
    </div>
  );
}
